import { React, useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../../PageTitle";
import useAxios from "../../../../utilities/useAxios";
import { toKoreaApiDateTime } from "../../../../utilities/koreaTime";
import { fetchKoreanHolidays } from "../../../../utilities/koreanHolidays";
import { createCalendarHolidayRenders } from "../../calendar/calendarHolidayCellRender";
import {
	Button,
	Calendar,
	Card,
	Checkbox,
	ConfigProvider,
	DatePicker,
	Form,
	Input,
	InputNumber,
	List,
	Modal,
	Select,
	Space,
	Tag,
	TimePicker,
	Typography,
	Upload,
	message,
} from "antd";
import { LeftOutlined, RightOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter.js";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
import {
	APPLICATION_KIND_OPERATION,
	APPLICATION_KIND_OPTIONS,
	APPLICATION_KIND_RENTAL,
	API_RENTAL_APPLY_TYPES,
	DRIVER_LICENSE_TYPE_OPTIONS,
	MAX_RENTAL_REQUESTED_COUNT,
	RENTAL_APPLY_CHECK_LABELS,
	RENTAL_APPLY_NOTICE,
	RENTAL_CAR_TYPE_OTHER,
	attachApplyCalendarMeta,
	compareCalendarItems,
	formatCalendarBarTimeParen,
	formatScheduleRangeLabel,
	getApplyScheduleCarLabel,
	getApplyScheduleColor,
	isDateDisabledForApply,
	truncateCellTagText,
} from "./rentalCarApplyUtils";
import useIsMobile from "../../../../hooks/useIsMobile";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("ko");

const { Option } = Select;
const { Text } = Typography;

const TIME_PICKER_PROPS = { format: "HH:mm", minuteStep: 30 };
const API_APPLY_CALENDAR = "/utils/rental/car/schedule/apply-calendar/";
const EMPTY_DAY_ITEMS = Object.freeze([]);
const MAX_OFFICIAL_DOCS = 5;
const MAX_OFFICIAL_DOC_BYTES = 50 * 1024 * 1024;
const OFFICIAL_DOC_EXT = /\.(pdf|jpe?g|png|gif|webp|bmp|hwp|hwpx|docx?)$/i;

const multipartConfig = {
	transformRequest: [
		(data, headers) => {
			delete headers["Content-Type"];
			return data;
		},
	],
};

const defaultStartTime = () => dayjs().hour(9).minute(0).second(0);
const defaultEndTime = () => dayjs().hour(18).minute(0).second(0);

const ApplyRentalCar = () => {
	const [form] = Form.useForm();
	const selectedCarTypeId = Form.useWatch("requested_apply_type", form);
	const applicationKind = Form.useWatch("application_kind", form);
	const [schedules, setSchedules] = useState([]);
	const [applyCarTypes, setApplyCarTypes] = useState([]);
	const [selectedDate, setSelectedDate] = useState(dayjs());
	const [calendarPanelDate, setCalendarPanelDate] = useState(() =>
		dayjs(),
	);
	const [holidayMap, setHolidayMap] = useState({});
	const isMobile = useIsMobile();
	const [dayModalVisible, setDayModalVisible] = useState(false);
	const [applyModalVisible, setApplyModalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [agreeFirst, setAgreeFirst] = useState(false);
	const [agreeSecond, setAgreeSecond] = useState(false);
	const [agreeThird, setAgreeThird] = useState(false);
	const [officialDocs, setOfficialDocs] = useState([]);

	useEffect(() => {
		loadSchedules();
		loadApplyCarTypes();
	}, []);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const map = await fetchKoreanHolidays(
					calendarPanelDate.year(),
					calendarPanelDate.month() + 1,
				);
				if (!cancelled) {
					setHolidayMap(map);
				}
			} catch (error) {
				console.error(error);
				if (!cancelled) {
					setHolidayMap({});
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [calendarPanelDate]);

	const loadApplyCarTypes = async () => {
		try {
			const response = await useAxios.get(
				API_RENTAL_APPLY_TYPES,
			);
			if (response.status === 200) {
				setApplyCarTypes(
					response.data.apply_types || [],
				);
			}
		} catch (error) {
			message.error("차량 종류를 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const loadSchedules = async () => {
		try {
			const response = await useAxios.get(API_APPLY_CALENDAR);
			if (response.status === 200) {
				setSchedules(
					(
						response.data
							.rental_car_schedule ||
						[]
					).map(attachApplyCalendarMeta),
				);
			}
		} catch (error) {
			message.error("일정을 불러오는데 실패했습니다.");
			console.error(error);
		}
	};

	const calendarItemsByDay = useMemo(() => {
		const map = new Map();
		const sorted = [...schedules].sort(compareCalendarItems);
		for (const item of sorted) {
			let cursor = dayjs(item._dayStartKey);
			const end = dayjs(item._dayEndKey);
			if (!cursor.isValid() || !end.isValid()) {
				continue;
			}
			let count = 0;
			while (
				cursor.isSameOrBefore(end, "day") &&
				count < 370
			) {
				const key = cursor.format("YYYY-MM-DD");
				const list = map.get(key);
				if (list) {
					list.push(item);
				} else {
					map.set(key, [item]);
				}
				cursor = cursor.add(1, "day");
				count += 1;
			}
		}
		return map;
	}, [schedules]);

	const getDaySchedules = useCallback(
		(date) =>
			calendarItemsByDay.get(date.format("YYYY-MM-DD")) ||
			EMPTY_DAY_ITEMS,
		[calendarItemsByDay],
	);

	const scheduleLaneMap = useMemo(() => {
		const laneMap = {};
		const laneEndMs = [];
		[...schedules]
			.sort(compareCalendarItems)
			.forEach((schedule) => {
				const startMs = schedule._dayStart.valueOf();
				const endMs = schedule._dayEnd.valueOf();
				let laneIndex = laneEndMs.findIndex(
					(laneEnd) => startMs > laneEnd,
				);
				if (laneIndex === -1) {
					laneIndex = laneEndMs.length;
				}
				laneEndMs[laneIndex] = endMs;
				laneMap[schedule._calendarKey] = laneIndex;
			});
		return laneMap;
	}, [schedules]);

	const selectedCarType = applyCarTypes.find(
		(type) => Number(type.id) === Number(selectedCarTypeId),
	);
	const isMultipleCarType = Boolean(selectedCarType?.is_multiple);
	const isOtherCarType = selectedCarType?.name === RENTAL_CAR_TYPE_OTHER;

	const handleDateSelect = (date) => {
		setSelectedDate(date);
		setDayModalVisible(true);
	};

	const openApplyModal = () => {
		form.resetFields();
		form.setFieldsValue({
			start_date: selectedDate,
			end_date: selectedDate,
			start_time: defaultStartTime(),
			end_time: defaultEndTime(),
			application_kind: APPLICATION_KIND_RENTAL,
			requested_count: 1,
		});
		setAgreeFirst(false);
		setAgreeSecond(false);
		setOfficialDocs([]);
		setApplyModalVisible(true);
	};

	const handleSubmit = async (values) => {
		const startDateTime = values.start_date
			.hour(values.start_time.hour())
			.minute(values.start_time.minute())
			.second(0)
			.millisecond(0);
		const endDateTime = values.end_date
			.hour(values.end_time.hour())
			.minute(values.end_time.minute())
			.second(0)
			.millisecond(0);

		if (!endDateTime.isAfter(startDateTime)) {
			message.error(
				"대여 마감 일시는 시작 일시보다 이후여야 합니다.",
			);
			return;
		}

		const isOperation =
			values.application_kind === APPLICATION_KIND_OPERATION;
		const selectedType = applyCarTypes.find(
			(type) =>
				Number(type.id) ===
				Number(values.requested_apply_type),
		);
		const isOtherCarTypeName =
			selectedType?.name === RENTAL_CAR_TYPE_OTHER;
		const requestedCount = selectedType?.is_multiple
			? Number(values.requested_count) || 1
			: 1;

		setLoading(true);
		try {
			const formData = new FormData();
			formData.append(
				"start_datetime",
				toKoreaApiDateTime(startDateTime),
			);
			formData.append(
				"end_datetime",
				toKoreaApiDateTime(endDateTime),
			);
			formData.append(
				"application_kind",
				values.application_kind,
			);
			formData.append("title", values.title);
			formData.append("route", values.route);
			formData.append(
				"driver",
				isOperation ? "" : values.driver,
			);
			formData.append(
				"driver_license_type",
				isOperation
					? ""
					: values.driver_license_type || "",
			);
			formData.append(
				"applicant_name",
				values.applicant_name,
			);
			formData.append(
				"applicant_contact",
				values.applicant_contact,
			);
			formData.append(
				"requested_apply_type",
				values.requested_apply_type,
			);
			formData.append(
				"custom_car_type",
				isOtherCarTypeName
					? values.custom_car_type
					: "",
			);
			formData.append(
				"requested_count",
				String(requestedCount),
			);
			officialDocs.forEach((file) => {
				formData.append("files", file, file.name);
			});

			const response = await useAxios.post(
				"/utils/rental/car/apply/",
				formData,
				multipartConfig,
			);

			if (response.status === 201) {
				message.success(
					"차량 대여 신청이 완료되었습니다.",
				);
				setApplyModalVisible(false);
				form.resetFields();
				setAgreeFirst(false);
				setAgreeSecond(false);
				setOfficialDocs([]);
				setDayModalVisible(false);
			}
		} catch (error) {
			const apiError = error.response?.data?.error;
			message.error(
				(typeof apiError === "string" && apiError) ||
					"신청 처리 중 오류가 발생했습니다.",
			);
		} finally {
			setLoading(false);
		}
	};

	const renderCalendarDateBody = useCallback(
		(date) => {
			const daySchedules = getDaySchedules(date);
			if (!daySchedules.length) {
				return null;
			}

			if (isMobile) {
				return (
					<div
						className="utils-rent_sanctuary__calendar-cell"
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							gap: 2,
							width: "100%",
						}}>
						{daySchedules.map(
							(schedule) => (
								<Tag
									key={
										schedule._calendarKey
									}
									color={getApplyScheduleColor(
										schedule,
									)}
									style={{
										margin: 0,
										maxWidth: "100%",
										fontSize: "10px",
										lineHeight: 1.2,
										padding: "0 4px",
									}}>
									{truncateCellTagText(
										getApplyScheduleCarLabel(
											schedule,
										),
									)}
								</Tag>
							),
						)}
					</div>
				);
			}

			const daySchedulesByLane = daySchedules.reduce(
				(acc, schedule) => {
					const laneIndex =
						scheduleLaneMap[
							schedule._calendarKey
						] ?? 0;
					acc[laneIndex] = schedule;
					return acc;
				},
				{},
			);
			const laneIndexes =
				Object.keys(daySchedulesByLane).map(Number);
			const maxLaneIndex =
				laneIndexes.length > 0
					? Math.max(...laneIndexes)
					: -1;

			return (
				<div className="utils-rent_sanctuary__calendar-cell">
					{Array.from(
						{ length: maxLaneIndex + 1 },
						(_, laneIndex) => {
							const schedule =
								daySchedulesByLane[
									laneIndex
								];
							if (!schedule) {
								return (
									<div
										key={`${date.format("YYYY-MM-DD")}-empty-${laneIndex}`}
										style={{
											height: "24.75px",
											marginBottom:
												"2px",
										}}
									/>
								);
							}

							const isStartDate =
								date.isSame(
									schedule._dayStart,
									"day",
								);
							const isEndDate =
								date.isSame(
									schedule._dayEnd,
									"day",
								);
							const isSingleDay =
								isStartDate &&
								isEndDate;
							const isMiddleDate =
								!isStartDate &&
								!isEndDate;
							const timeParen =
								formatCalendarBarTimeParen(
									schedule,
								);
							const barTitle =
								timeParen
									? `${getApplyScheduleCarLabel(schedule)} ${timeParen}`
									: getApplyScheduleCarLabel(
											schedule,
										);

							return (
								<div
									key={
										schedule._calendarKey
									}
									className="utils-rent_sanctuary__calendar-event"
									style={{
										backgroundColor:
											getApplyScheduleColor(
												schedule,
											),
										color: "#fff",
										padding: "2px 6px",
										marginBottom:
											"2px",
										fontSize: "11px",
										borderRadius:
											isSingleDay
												? "4px"
												: isStartDate
													? "4px 0 0 4px"
													: isEndDate
														? "0 4px 4px 0"
														: "0",
										marginLeft: isStartDate
											? "4px"
											: "0",
										marginRight:
											isEndDate
												? "4px"
												: "0",
										width:
											(isStartDate &&
												!isSingleDay) ||
											(isEndDate &&
												!isSingleDay)
												? "calc(100% - 4px)"
												: isSingleDay
													? "calc(100% - 8px)"
													: "100%",
										borderLeft: isStartDate
											? "3px solid #666"
											: "none",
										overflow: "hidden",
										textOverflow:
											"ellipsis",
										whiteSpace: "nowrap",
									}}>
									{isStartDate && (
										<span
											style={{
												fontWeight: 500,
												fontSize: "1.2em",
											}}>
											{
												barTitle
											}
										</span>
									)}
									{(isMiddleDate ||
										(isEndDate &&
											!isStartDate)) && (
										<span
											style={{
												opacity: 0,
												fontSize: "1.2em",
											}}>
											{
												barTitle
											}
										</span>
									)}
								</div>
							);
						},
					)}
				</div>
			);
		},
		[getDaySchedules, isMobile, scheduleLaneMap],
	);

	const { fullCellRender } = useMemo(
		() =>
			createCalendarHolidayRenders({
				holidayMap,
				renderDateContent: renderCalendarDateBody,
			}),
		[holidayMap, renderCalendarDateBody],
	);

	const daySchedules = getDaySchedules(selectedDate);
	const canApplySelectedDate = !isDateDisabledForApply(selectedDate);

	return (
		<ConfigProvider
			locale={locale}
			theme={{
				components: {
					Form: {
						itemMarginBottom: 12,
						verticalLabelPadding: "0 0 4px",
					},
				},
			}}>
			<PageTitle title="차량 대여 신청" />
			<div className="utils-rent_sanctuary__container">
				<div className="utils-rent_sanctuary__header">
					<h1 className="utils-accessed_car__header__title">
						차량 대여 신청
					</h1>
				</div>
				<div className="utils-rent_sanctuary__content-container">
					<Card className="utils-rent_sanctuary__calendar-card">
						<div className="utils-rent_car_calendar-scope">
							<Calendar
								className="utils-calendar-hide-outside-month-week"
								headerRender={({
									value,
									onChange,
								}) => {
									const year =
										value.year();
									const month =
										value.month();
									const months =
										[];
									const yearOptions =
										[];
									for (
										let i = 0;
										i <
										12;
										i++
									) {
										months.push(
											<Select.Option
												key={
													i
												}
												value={
													i
												}>
												{i +
													1}

												월
											</Select.Option>,
										);
									}
									for (
										let i =
											year -
											10;
										i <
										year +
											10;
										i++
									) {
										yearOptions.push(
											<Select.Option
												key={
													i
												}
												value={
													i
												}>
												{
													i
												}

												년
											</Select.Option>,
										);
									}
									return (
										<Space
											style={{
												padding: 8,
											}}>
											<Button
												size="middle"
												onClick={() => {
													onChange(
														value.subtract(
															1,
															"month",
														),
													);
													setDayModalVisible(
														false,
													);
												}}>
												<LeftOutlined />
											</Button>
											<Select
												size="middle"
												value={
													year
												}
												onChange={(
													newYear,
												) =>
													onChange(
														value
															.clone()
															.year(
																newYear,
															),
													)
												}
												style={{
													width: 100,
												}}>
												{
													yearOptions
												}
											</Select>
											<Select
												size="middle"
												value={
													month
												}
												onChange={(
													newMonth,
												) =>
													onChange(
														value
															.clone()
															.month(
																newMonth,
															),
													)
												}
												style={{
													width: 80,
												}}>
												{
													months
												}
											</Select>
											<Button
												size="middle"
												onClick={() =>
													onChange(
														dayjs(),
													)
												}>
												오늘
											</Button>
											<Button
												size="middle"
												onClick={() => {
													onChange(
														value.add(
															1,
															"month",
														),
													);
													setDayModalVisible(
														false,
													);
												}}>
												<RightOutlined />
											</Button>
										</Space>
									);
								}}
								fullCellRender={
									fullCellRender
								}
								onPanelChange={(
									date,
								) => {
									setCalendarPanelDate(
										date,
									);
								}}
								onSelect={
									handleDateSelect
								}
								mode="month"
								locale={{
									lang: {
										locale: "ko",
										monthBeforeYear: true,
										yearFormat: "YYYY년",
										monthFormat:
											"M월",
										weekFormat: "wo",
										weekStart: 0,
										shortWeekDays:
											[
												"일",
												"월",
												"화",
												"수",
												"목",
												"금",
												"토",
											],
										shortMonths:
											[
												"1월",
												"2월",
												"3월",
												"4월",
												"5월",
												"6월",
												"7월",
												"8월",
												"9월",
												"10월",
												"11월",
												"12월",
											],
									},
								}}
							/>
						</div>
					</Card>
				</div>
			</div>

			<Modal
				title={
					<div
						style={{
							fontSize: isMobile
								? "16px"
								: "18px",
							fontWeight: 600,
						}}>
						{selectedDate.format(
							"YYYY년 MM월 DD일",
						)}{" "}
						일정
					</div>
				}
				open={dayModalVisible}
				onCancel={() => setDayModalVisible(false)}
				footer={null}
				width={isMobile ? "95%" : 800}
				style={{
					top: isMobile ? 10 : 20,
					maxWidth: isMobile ? "100%" : 800,
				}}
				className="utils-rent_sanctuary__schedule-modal"
				destroyOnClose
				maskClosable
				centered={isMobile}>
				<div
					style={{
						maxHeight: isMobile
							? "80vh"
							: "70vh",
						overflowY: "auto",
						padding: "8px 0",
					}}>
					{daySchedules.length > 0 ? (
						<List
							dataSource={
								daySchedules
							}
							renderItem={(
								schedule,
							) => (
								<List.Item
									style={{
										padding: "12px 0",
										borderBottom:
											"1px solid #f0f0f0",
									}}>
									<div
										style={{
											display: "flex",
											flexWrap: "wrap",
											gap: 8,
											alignItems: "center",
										}}>
										<Tag
											color={getApplyScheduleColor(
												schedule,
											)}>
											{getApplyScheduleCarLabel(
												schedule,
											)}
										</Tag>
										<Tag color="blue">
											{formatScheduleRangeLabel(
												schedule,
											)}
										</Tag>
									</div>
								</List.Item>
							)}
						/>
					) : (
						<p className="utils-rent_sanctuary__daily-schedule__empty">
							해당 날짜의 일정이
							없습니다.
						</p>
					)}
					<div
						style={{
							marginTop: 20,
							textAlign: "center",
						}}>
						<Button
							type="primary"
							size={
								isMobile
									? "middle"
									: "large"
							}
							disabled={
								!canApplySelectedDate
							}
							onClick={openApplyModal}
							style={{
								minWidth: 150,
								width: isMobile
									? "100%"
									: "auto",
							}}>
							차량 대여 신청
						</Button>
					</div>
				</div>
			</Modal>

			<Modal
				title="차량 대여 신청"
				open={applyModalVisible}
				onCancel={() => {
					setApplyModalVisible(false);
					form.resetFields();
					setAgreeFirst(false);
					setAgreeSecond(false);
					setOfficialDocs([]);
				}}
				footer={null}
				width={isMobile ? "95%" : 600}
				centered={isMobile}
				destroyOnClose>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}>
					<Form.Item
						name="start_date"
						label="시작일"
						rules={[
							{
								required: true,
								message: "시작일을 선택해주세요",
							},
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{
								width: "100%",
							}}
							format="YYYY-MM-DD"
							disabledDate={
								isDateDisabledForApply
							}
							allowClear={false}
						/>
					</Form.Item>
					<Form.Item
						name="start_time"
						label="시작 시간"
						rules={[
							{
								required: true,
								message: "시작 시간을 선택해주세요",
							},
						]}>
						<TimePicker
							inputReadOnly={isMobile}
							style={{
								width: "100%",
							}}
							{...TIME_PICKER_PROPS}
							allowClear={false}
						/>
					</Form.Item>
					<Form.Item
						name="end_date"
						label="마감일"
						dependencies={["start_date"]}
						rules={[
							{
								required: true,
								message: "마감일을 선택해주세요",
							},
							({
								getFieldValue,
							}) => ({
								validator(
									_,
									value,
								) {
									const start =
										getFieldValue(
											"start_date",
										);
									if (
										!value ||
										!start ||
										!value.isBefore(
											start,
											"day",
										)
									) {
										return Promise.resolve();
									}
									return Promise.reject(
										new Error(
											"마감일은 시작일과 같거나 이후여야 합니다.",
										),
									);
								},
							}),
						]}>
						<DatePicker
							inputReadOnly={isMobile}
							style={{
								width: "100%",
							}}
							format="YYYY-MM-DD"
							disabledDate={
								isDateDisabledForApply
							}
							allowClear={false}
						/>
					</Form.Item>
					<Form.Item
						name="end_time"
						label="마감 시간"
						rules={[
							{
								required: true,
								message: "마감 시간을 선택해주세요",
							},
						]}>
						<TimePicker
							inputReadOnly={isMobile}
							style={{
								width: "100%",
							}}
							{...TIME_PICKER_PROPS}
							allowClear={false}
						/>
					</Form.Item>
					<Form.Item
						name="application_kind"
						label="대여 종류"
						rules={[
							{
								required: true,
								message: "대여 종류를 선택해주세요",
							},
						]}>
						<Select placeholder="대여 종류를 선택하세요">
							{APPLICATION_KIND_OPTIONS.map(
								(kind) => (
									<Option
										key={
											kind
										}
										value={
											kind
										}>
										{
											kind
										}
									</Option>
								),
							)}
						</Select>
					</Form.Item>
					<Form.Item
						name="title"
						label="대여 내역"
						rules={[
							{
								required: true,
								message: "대여 내역을 입력해주세요",
							},
						]}>
						<Input placeholder="대여 내역을 입력하세요" />
					</Form.Item>
					<Form.Item
						name="route"
						label="운행경로"
						rules={[
							{
								required: true,
								message: "운행경로를 입력해주세요",
							},
						]}>
						<Input placeholder="예: 교회 → 여주연수원 → 교회" />
					</Form.Item>
					{applicationKind !==
						APPLICATION_KIND_OPERATION && (
						<>
							<Form.Item
								name="driver"
								label="운전자 이름"
								rules={[
									{
										required: true,
										message: "운전자 이름을 입력해주세요",
									},
								]}>
								<Input placeholder="운전자 이름을 입력하세요" />
							</Form.Item>
							<Form.Item
								name="driver_license_type"
								label="운전자 면허 종류"
								rules={[
									{
										required: true,
										message: "운전자 면허 종류를 선택해주세요",
									},
								]}>
								<Select placeholder="면허 종류를 선택하세요">
									{DRIVER_LICENSE_TYPE_OPTIONS.map(
										(
											licenseType,
										) => (
											<Option
												key={
													licenseType
												}
												value={
													licenseType
												}>
												{
													licenseType
												}
											</Option>
										),
									)}
								</Select>
							</Form.Item>
						</>
					)}
					<Form.Item
						name="applicant_name"
						label="신청자 이름"
						rules={[
							{
								required: true,
								message: "신청자 이름을 입력해주세요",
							},
						]}>
						<Input placeholder="신청자 이름을 입력하세요" />
					</Form.Item>
					<Form.Item
						name="applicant_contact"
						label="신청자 연락처"
						rules={[
							{
								required: true,
								message: "신청자 연락처를 입력해주세요",
							},
							{
								pattern: /^[0-9-]+$/,
								message: "숫자와 하이픈(-)만 입력 가능합니다",
							},
						]}>
						<Input placeholder="연락처를 입력하세요 (예: 010-1234-5678)" />
					</Form.Item>
					<Form.Item
						name="requested_apply_type"
						label="차량 종류"
						rules={[
							{
								required: true,
								message: "차량 종류를 선택해주세요",
							},
						]}>
						<Select
							placeholder="차량 종류를 선택하세요"
							onChange={(value) => {
								const type =
									applyCarTypes.find(
										(
											item,
										) =>
											Number(
												item.id,
											) ===
											Number(
												value,
											),
									);
								if (
									!type?.is_multiple
								) {
									form.setFieldsValue(
										{
											requested_count: 1,
										},
									);
								}
							}}>
							{applyCarTypes.map(
								(carType) => (
									<Option
										key={
											carType.id
										}
										value={
											carType.id
										}>
										{
											carType.name
										}
										{carType.is_multiple
											? " (복수 대여)"
											: ""}
									</Option>
								),
							)}
						</Select>
					</Form.Item>
					{isOtherCarType && (
						<Form.Item
							name="custom_car_type"
							label="차량 종류 직접 입력"
							rules={[
								{
									required: true,
									message: "차량 종류를 입력해주세요",
								},
							]}>
							<Input placeholder="신청할 차량 종류를 입력하세요" />
						</Form.Item>
					)}
					{isMultipleCarType && (
						<Form.Item
							name="requested_count"
							label="차량 대수"
							rules={[
								{
									required: true,
									message: "차량 대수를 입력해주세요",
								},
							]}>
							<InputNumber
								min={1}
								max={
									MAX_RENTAL_REQUESTED_COUNT
								}
								style={{
									width: "100%",
								}}
								placeholder="대여할 차량 대수를 입력하세요"
							/>
						</Form.Item>
					)}
					<Form.Item label="공문/협조문">
						<Upload
							multiple
							beforeUpload={(
								file,
							) => {
								if (
									!OFFICIAL_DOC_EXT.test(
										file.name,
									)
								) {
									message.error(
										"PDF, 이미지, HWP, Word 파일만 업로드할 수 있습니다.",
									);
									return Upload.LIST_IGNORE;
								}
								if (
									file.size >
									MAX_OFFICIAL_DOC_BYTES
								) {
									message.error(
										"파일당 크기는 50MB를 넘을 수 없습니다.",
									);
									return Upload.LIST_IGNORE;
								}
								if (
									officialDocs.length >=
									MAX_OFFICIAL_DOCS
								) {
									message.error(
										`첨부 파일은 최대 ${MAX_OFFICIAL_DOCS}개까지 업로드할 수 있습니다.`,
									);
									return Upload.LIST_IGNORE;
								}
								setOfficialDocs(
									(
										prev,
									) => [
										...prev,
										file,
									],
								);
								return false;
							}}
							onRemove={(file) => {
								setOfficialDocs(
									(
										prev,
									) =>
										prev.filter(
											(
												item,
											) =>
												item.uid !==
													file.uid &&
												item !==
													file,
										),
								);
							}}
							fileList={officialDocs}>
							<Button
								icon={
									<UploadOutlined />
								}>
								파일 선택 (선택)
							</Button>
						</Upload>
					</Form.Item>
					<Form.Item>
						<Checkbox
							checked={agreeFirst}
							onChange={(e) =>
								setAgreeFirst(
									e.target
										.checked,
								)
							}>
							{
								RENTAL_APPLY_CHECK_LABELS[0]
							}
						</Checkbox>
					</Form.Item>
					<Form.Item>
						<Checkbox
							checked={agreeSecond}
							onChange={(e) =>
								setAgreeSecond(
									e.target
										.checked,
								)
							}>
							{
								RENTAL_APPLY_CHECK_LABELS[1]
							}
						</Checkbox>
					</Form.Item>
					<Form.Item>
						<Checkbox
							checked={agreeThird}
							onChange={(e) =>
								setAgreeThird(
									e.target
										.checked,
								)
							}
							style={{
								fontSize: 14,
								lineHeight: 1.5,
								color: "#dd0000",
							}}>
							{RENTAL_APPLY_NOTICE}
						</Checkbox>
					</Form.Item>
					<Form.Item style={{ marginBottom: 0 }}>
						<Button
							type="primary"
							htmlType="submit"
							loading={loading}
							disabled={
								!agreeFirst ||
								!agreeSecond ||
								!agreeThird
							}
							block>
							신청하기
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default ApplyRentalCar;
